import { TranscriptItem } from 'types';
import { logger } from 'src/lib/logger'
import { sanitizeProviderErrorMessage } from '../src/lib/safe-error-message.js'
import {
    ensureSessionCapability,
    getOrCreateClientSessionId,
    SESSION_HEADER,
} from '../src/core/session/session-identity.js'
import { acceptEmbeddedSessionProjection } from '../src/lib/accept-session-projection-response.js'
import { clientSessionViewStore } from './clientSessionViewStore.js'

/**
 * Build a real Error from a parsed server-streaming error payload.
 * Extracted so the logger serializes name + message + (optional) code
 * instead of dropping an `unknown`-typed field into the context bag.
 *
 * The message is routed through the canonical provider-error sanitizer
 * so a payload that contains `api_key=...` or a `AIza...` token does
 * not leak through the synthesized Error.message into the log stream.
 */
export function buildServerStreamingError(parsed: Record<string, unknown>): Error {
    const rawMessage =
        typeof parsed.error === 'string'
            ? parsed.error
            : typeof parsed.message === 'string'
                ? parsed.message
                : 'Unknown server streaming error';
    const errorMessage = sanitizeProviderErrorMessage(rawMessage);
    const errorCode =
        typeof parsed.code === 'string' || typeof parsed.code === 'number'
            ? String(parsed.code)
            : undefined;
    const err = new Error(errorMessage);
    err.name = 'ServerStreamingError';
    if (errorCode) (err as Error & { code?: string }).code = errorCode;
    return err;
}

interface AIBrainResponse {
    success: boolean;
    text?: string;
    error?: string;
    data?: unknown;
}

export interface AgentResponse {
    success: boolean;
    output?: string;
    agent?: string;
    model?: string;
    metadata?: {
        stage?: string;
        error?: string;
        [key: string]: unknown;
    };
    error?: string;
    code?: string;
    owner?: string;
}

export interface StreamingMetrics {
    totalStreams: number;
    successfulStreams: number;
    partialStreams: number;
    failedStreams: number;
    recoverableErrors: number;
    catastrophicErrors: number;
    averageChunkCount: number;
    averageMetadataCount: number;
    lastStreamDuration: number;
    lastStreamSuccess: boolean;
}

function parseHttpErrorBody(
    status: number,
    errorText: string
): { errorMsg: string; errorDetails?: unknown; code?: string; owner?: string } {
    let errorMsg = `Server error: ${status}`;
    let errorDetails: unknown;
    let code: string | undefined;
    let owner: string | undefined;

    try {
        if (errorText.trim().startsWith('{') || errorText.trim().startsWith('[')) {
            const errorJson = JSON.parse(errorText) as {
                error?: string;
                message?: string;
                details?: unknown;
                code?: string;
                owner?: string;
            };
            errorMsg = errorJson.error || errorJson.message || errorMsg;
            errorDetails = errorJson.details;
            code = typeof errorJson.code === 'string' ? errorJson.code : undefined;
            owner = typeof errorJson.owner === 'string' ? errorJson.owner : undefined;
        } else {
            errorMsg = errorText || errorMsg;
        }
    } catch {
        errorMsg = errorText && errorText.length < 500 ? errorText : errorMsg;
    }

    return {
        errorMsg,
        ...(errorDetails !== undefined ? { errorDetails } : {}),
        ...(code ? { code } : {}),
        ...(owner ? { owner } : {}),
    };
}

function acceptProjectionFromJsonText(value: string, source: string): void {
    try {
        acceptEmbeddedSessionProjection(JSON.parse(value) as unknown, source)
    } catch {
        // Non-JSON error responses do not carry a projection envelope.
    }
}

/** The plate seals this before the greeting. The replacement session keeps it on the client. */
function sealedReplyLocale(): { uiLocale: 'nb' | 'en' } | Record<string, never> {
    const uiLocale = clientSessionViewStore.getSnapshot().uiLocale
    if (uiLocale === 'nb' || uiLocale === 'en') return { uiLocale }
    return {}
}

export class AIBrainService {
    private baseUrl: string;
    private sessionId: string;
    private streamingMetrics: StreamingMetrics = {
        totalStreams: 0,
        successfulStreams: 0,
        partialStreams: 0,
        failedStreams: 0,
        recoverableErrors: 0,
        catastrophicErrors: 0,
        averageChunkCount: 0,
        averageMetadataCount: 0,
        lastStreamDuration: 0,
        lastStreamSuccess: false
    };

    constructor(sessionId?: string) {
        this.baseUrl = this.determineBaseUrl();
        this.sessionId = sessionId || getOrCreateClientSessionId();
    }

    setSessionId(sessionId: string): void {
        this.sessionId = sessionId;
    }

    getSessionId(): string {
        return this.sessionId;
    }

    /**
     * Get streaming performance metrics
     */
    getStreamingMetrics(): StreamingMetrics {
        return { ...this.streamingMetrics };
    }

    /**
     * Reset streaming metrics (useful for debugging)
     */
    resetStreamingMetrics(): void {
        this.streamingMetrics = {
            totalStreams: 0,
            successfulStreams: 0,
            partialStreams: 0,
            failedStreams: 0,
            recoverableErrors: 0,
            catastrophicErrors: 0,
            averageChunkCount: 0,
            averageMetadataCount: 0,
            lastStreamDuration: 0,
            lastStreamSuccess: false
        };
    }

    private determineBaseUrl(): string {
        const metaEnv = (globalThis as any).import?.meta?.env;

        if (metaEnv) {
            // In development allow overriding with VITE_AGENT_API_URL
            if (metaEnv.DEV) {
                const custom = metaEnv.VITE_AGENT_API_URL as string | undefined;
                if (custom) {
                    return custom.replace(/\/api\/chat$/, '');
                }
                return 'http://localhost:3002';
            }

            // In production tests, prefer relative path so fetch hits proxy
            if (metaEnv.PROD) {
                return '';
            }
        }

        // 1. Use explicit env var if available (e.g. in Vercel)
        if (process.env.NEXT_PUBLIC_API_URL) {
            return process.env.NEXT_PUBLIC_API_URL;
        }

        // 2. In browser, use relative path (proxy) or absolute URL based on window
        if (typeof window !== 'undefined') {
            // If local, assume API is on port 3002 (as per your setup)
            // But if we are using a proxy in vite.config, relative path is better
            // Let's stick to relative path '/api' which works if proxy is set up or if served from same origin
            return '';
        }

        // 3. Fallback for server-side rendering (SSR)
        return 'http://localhost:3002';
    }

    /**
     * Sends a message to the server-side AI Brain API.
     * This API orchestrates the multi-agent system.
     */
    async sendMessage(
        message: string,
        _history: TranscriptItem[],
        attachment?: { mimeType: string; data: string }
    ): Promise<AIBrainResponse> {
        try {
            const payload = {
                messages: [{
                    role: 'user',
                    content: message,
                    ...(attachment ? { attachments: [attachment] } : {}),
                }],
                sessionId: this.sessionId,
                ...sealedReplyLocale(),
            };

            const url = `${this.baseUrl}/api/chat/unified`;
            logger.debug('[AIBrainService] Sending request to:', { url });

            // Add Vercel protection bypass for production API calls
            const headers: Record<string, string> = {
                'Content-Type': 'application/json',
                [SESSION_HEADER]: this.sessionId,
            };

            // Add bypass token for Vercel-protected deployments
            if (typeof window !== 'undefined' && window.location.hostname.includes('vercel.app')) {
                headers['x-vercel-protection-bypass'] = 'bypass-for-api-routes';
            }

            await ensureSessionCapability(this.sessionId);
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers,
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                const { errorMsg, errorDetails, code, owner } = parseHttpErrorBody(response.status, errorText);
                if (code !== 'SESSION_OWNERSHIP_CONFLICT') {
                    acceptProjectionFromJsonText(errorText, 'AIBrainService.sendMessage:error');
                }
                if (!code && !errorText.trim().startsWith('{')) {
                    logger.warn('[AIBrainService] Non-JSON error response', {
                        status: response.status,
                        contentType: response.headers.get('content-type'),
                        preview: errorText.substring(0, 500),
                    });
                }
                return {
                    success: false,
                    error: errorMsg,
                    ...(code ? { code } : {}),
                    ...(owner ? { owner } : {}),
                    ...(errorDetails !== undefined ? { details: errorDetails } : {}),
                };
            }

            const data = (await response.json()) ?? {};
            acceptEmbeddedSessionProjection(data, 'AIBrainService.sendMessage');
            const responseData = data as Record<string, unknown>
            const text = typeof responseData.text === 'string' ? responseData.text : typeof responseData.message === 'string' ? responseData.message : undefined;
            const result: AIBrainResponse = {
                success: true,
                data: responseData
            };
            if (text !== undefined) {
                result.text = text;
            }
            return result;

        } catch (error: any) {
            // Detect connection refused / network errors
            const err = error instanceof Error ? error : null
            const errorMsg = err?.message || '';
            const isConnectionRefused =
                errorMsg.includes('ECONNREFUSED') ||
                errorMsg.includes('Failed to fetch') ||
                errorMsg.includes('NetworkError') ||
                (err as { code?: string } | null)?.code === 'ECONNREFUSED';

            if (isConnectionRefused) {
                const isDev = typeof window !== 'undefined' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                
                const helpMessage = isDev 
                    ? 'API server not running. Start it with: pnpm dev:api:3002'
                    : 'Unable to connect to API server. Please try again later.';
                
                logger.warn('[AIBrainService] Connection error', { isDev, originalError: errorMsg });
                return {
                    success: false,
                    error: helpMessage
                };
            }

            logger.error('[AIBrainService] Error', error instanceof Error ? error : undefined);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }

    /**
     * Chat method that matches the expected interface from App.tsx
     * Sends messages to the agent API and returns AgentResponse
     */
    async chat(
        messages: Array<{ role: string; content: string; attachments?: Array<{ mimeType: string; data: string }> }>
    ): Promise<AgentResponse> {
        try {
            const url = `${this.baseUrl}/api/chat/unified`;

            const payload = {
                messages: messages.slice(-1),
                sessionId: this.sessionId,
                ...sealedReplyLocale(),
            };

            await ensureSessionCapability(this.sessionId);
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: {
                    'Content-Type': 'application/json',
                    [SESSION_HEADER]: this.sessionId,
                },
                body: JSON.stringify(payload)
            });

            if (!response.ok) {
                const errorText = await response.text();
                const { errorMsg, errorDetails, code, owner } = parseHttpErrorBody(response.status, errorText);
                if (code !== 'SESSION_OWNERSHIP_CONFLICT') {
                    acceptProjectionFromJsonText(errorText, 'AIBrainService.chat:error');
                }
                if (response.status >= 500) {
                    logger.error('[AIBrainService] Server returned error', undefined, {
                        status: response.status,
                        message: errorMsg,
                        details: errorDetails,
                        code,
                    });
                } else {
                    logger.warn('[AIBrainService] Server returned error', {
                        status: response.status,
                        message: errorMsg,
                        code,
                    });
                }
                return {
                    success: false,
                    error: errorMsg,
                    ...(code ? { code } : {}),
                    ...(owner ? { owner } : {}),
                    ...(errorDetails !== undefined ? { details: errorDetails } : {}),
                };
            }

            const data = await response.json();
            acceptEmbeddedSessionProjection(data, 'AIBrainService.chat');
            return {
                success: data.success !== false,
                output: data.output || data.text || data.message,
                agent: data.agent,
                model: data.model,
                metadata: data.metadata
            };
        } catch (error: any) {
            // Detect connection refused / network errors
            const err = error instanceof Error ? error : null
            const errorMsg = err?.message || '';
            const isConnectionRefused =
                errorMsg.includes('ECONNREFUSED') ||
                errorMsg.includes('Failed to fetch') ||
                errorMsg.includes('NetworkError') ||
                (err as { code?: string } | null)?.code === 'ECONNREFUSED';

            if (isConnectionRefused) {
                const isDev = typeof window !== 'undefined' && 
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');
                
                const helpMessage = isDev 
                    ? 'API server not running. Start it with: pnpm dev:api:3002'
                    : 'Unable to connect to API server. Please try again later.';
                
                logger.warn('[AIBrainService] Connection error in chat', { isDev, originalError: errorMsg });
                return {
                    success: false,
                    error: helpMessage
                };
            }

            logger.error('[AIBrainService] Error in chat', error instanceof Error ? error : undefined);
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown error occurred'
            };
        }
    }

    /**
     * Streaming chat method that handles SSE responses
     * Streams agent responses progressively and calls onChunk for each chunk
     */
    async chatStream(
        messages: Array<{ role: string; content: string; attachments?: Array<{ mimeType: string; data: string }> }>,
        options?: {
            onChunk?: (text: string) => void;
            onMetadata?: (metadata: { type: string; toolCall?: unknown; reasoning?: string; message?: string; [key: string]: unknown }) => void;
            signal?: AbortSignal;
        }
    ): Promise<AgentResponse> {
        const streamStartTime = Date.now();
        let localRecoverableErrors = 0;
        let localCatastrophicErrors = 0;
        let localChunkCount = 0;
        let localMetadataCount = 0;

        try {
            this.streamingMetrics.totalStreams++;

            const url = `${this.baseUrl}/api/chat/unified`;

            const payload = {
                messages: messages.slice(-1),
                sessionId: this.sessionId,
                stream: true, // Enable streaming
                ...sealedReplyLocale(),
            };

            const streamHeaders: Record<string, string> = {
                'Content-Type': 'application/json',
                [SESSION_HEADER]: this.sessionId,
            };
            if (typeof window !== 'undefined' && window.location.hostname.includes('vercel.app')) {
                streamHeaders['x-vercel-protection-bypass'] = 'bypass-for-api-routes';
            }

            await ensureSessionCapability(this.sessionId);
            const response = await fetch(url, {
                method: 'POST',
                credentials: 'same-origin',
                headers: streamHeaders,
                body: JSON.stringify(payload),
                ...(options?.signal && { signal: options.signal })
            });

            if (!response.ok) {
                const errorText = await response.text();
                const { errorMsg, code, owner } = parseHttpErrorBody(response.status, errorText);
                if (code !== 'SESSION_OWNERSHIP_CONFLICT') {
                    acceptProjectionFromJsonText(errorText, 'AIBrainService.chatStream:error');
                }
                return {
                    success: false,
                    error: errorMsg,
                    ...(code ? { code } : {}),
                    ...(owner ? { owner } : {}),
                };
            }

            // Check if response is SSE stream
            const contentType = response.headers.get('content-type');
            if (!contentType || !contentType.includes('text/event-stream')) {
                // Fallback to non-streaming if not SSE
                const data = await response.json();
                acceptEmbeddedSessionProjection(data, 'AIBrainService.chatStream:fallback');
                return {
                    success: data.success !== false,
                    output: data.output || data.text || data.message,
                    agent: data.agent,
                    model: data.model,
                    metadata: data.metadata
                };
            }

            // Parse SSE stream with robust error handling
            const reader = response.body?.getReader();
            const decoder = new TextDecoder();
            let buffer = '';
            let accumulatedContent = '';
            let fullAccumulatedText = ''; // Dedicated accumulator for graceful degradation fallback
            let finalResult: AgentResponse | null = null;
            let chunkCount = 0;
            let metadataCount = 0;
            let recoverableErrors = 0;
            let catastrophicErrors = 0;

            logger.debug('[AIBrainService] Starting robust SSE stream parsing');

            // Robust SSE Parser with JSON reconstruction
            class SSEParser {
                private jsonBuffer = '';
                private braceCount = 0;
                private inString = false;
                private escapeNext = false;

                processChunk(chunk: string): Array<{ success: boolean; data?: unknown; error?: string }> {
                    const results: Array<{ success: boolean; data?: unknown; error?: string }> = [];
                    // CRITICAL FIX: Prepend buffered content from previous chunk to handle JSON split across TCP packets
                    let currentJson = this.jsonBuffer;

                    for (let i = 0; i < chunk.length; i++) {
                        const char = chunk[i];

                        // Track JSON structure
                        if (!this.inString) {
                            if (char === '{') this.braceCount++;
                            else if (char === '}') this.braceCount--;
                        }

                        // Track string state
                        if (char === '"' && !this.escapeNext) {
                            this.inString = !this.inString;
                        }
                        this.escapeNext = char === '\\' && !this.escapeNext;

                        currentJson += char;

                        // Check if we have a complete JSON object
                        if (this.braceCount === 0 && !this.inString && currentJson.trim()) {
                            try {
                                const parsed = JSON.parse(currentJson.trim());
                                results.push({ success: true, data: parsed });
                                currentJson = '';
                            } catch (error) {
                                // Check if this might be recoverable (partial JSON)
                                if (this.isLikelyPartialJson(currentJson)) {
                                    // Continue accumulating - don't treat as error yet
                                    continue;
                                } else {
                                    results.push({
                                        success: false,
                                        error: `Invalid JSON structure: ${currentJson.substring(0, 100)}...`
                                    });
                                    currentJson = '';
                                    this.reset();
                                }
                            }
                        }
                    }

                    // If we have remaining content, keep it for next chunk
                    if (currentJson.trim()) {
                        this.jsonBuffer = currentJson;
                    }

                    return results;
                }

                private isLikelyPartialJson(json: string): boolean {
                    const trimmed = json.trim();
                    // Likely partial if we have unmatched braces or unclosed strings
                    return this.braceCount > 0 || this.inString ||
                           (!trimmed.endsWith('}') && trimmed.includes('{'));
                }

                private reset(): void {
                    this.jsonBuffer = '';
                    this.braceCount = 0;
                    this.inString = false;
                    this.escapeNext = false;
                }

                getBufferedContent(): string {
                    return this.jsonBuffer;
                }

                hasBufferedContent(): boolean {
                    return this.jsonBuffer.length > 0;
                }
            }

            const sseParser = new SSEParser();

            if (reader) {
                try {
                    // eslint-disable-next-line no-constant-condition
                    while (true) {
                        const { done, value } = await reader.read();
                        if (done) break;

                        buffer += decoder.decode(value, { stream: true });
                        const lines = buffer.split('\n');
                        buffer = lines.pop() || '';

                        for (const line of lines) {
                            if (line.startsWith('data: ')) {
                                const dataText = line.slice(6).trim();
                                if (dataText && dataText !== '[DONE]') {
                                    // Process through robust parser
                                    const parseResults = sseParser.processChunk(dataText);

                                    for (const result of parseResults) {
                                        if (!result.success) {
                                            localRecoverableErrors++;
                                            logger.warn('[AIBrainService] Recoverable JSON parse error', {
                                                error: result.error,
                                                recoverableErrors: localRecoverableErrors,
                                                catastrophicErrors: localCatastrophicErrors,
                                                bufferSize: sseParser.getBufferedContent().length
                                            });
                                            continue; // Skip this chunk but continue streaming
                                        }

                                        const parsedRaw = result.data;
                                        const parsed = typeof parsedRaw === 'object' && parsedRaw !== null ? parsedRaw as Record<string, unknown> : {};
                                        localChunkCount++;

                                        // Check for streaming errors from server
                                        if (parsed.type === 'error') {
                                            localCatastrophicErrors++;
                                            const syntheticError = buildServerStreamingError(parsed);
                                            const errorCode = (syntheticError as Error & { code?: string }).code;
                                            logger.error(
                                                '[AIBrainService] Server streaming error',
                                                syntheticError,
                                                {
                                                    endpoint: '/api/chat/unified',
                                                    sessionIdPrefix: this.sessionId.slice(0, 12),
                                                    parsedType: parsed.type,
                                                    parsedKeys: Object.keys(parsed),
                                                    errorMessage: syntheticError.message,
                                                    errorCode,
                                                }
                                            );
                                            this.streamingMetrics.catastrophicErrors += localCatastrophicErrors;
                                            this.streamingMetrics.failedStreams++;
                                            this.streamingMetrics.lastStreamDuration = Date.now() - streamStartTime;
                                            this.streamingMetrics.lastStreamSuccess = false;
                                            return {
                                                success: false,
                                                error: syntheticError.message || 'Streaming error occurred'
                                            };
                                        }

                                        // Handle content chunks
                                        const content = typeof parsed.content === 'string' ? parsed.content : '';
                                        if (parsed.type === 'content' && content) {
                                            const previousLength = accumulatedContent.length;
                                            accumulatedContent += content;
                                            fullAccumulatedText += content; // Always append to ensure full text is preserved
                                            const newChunkLength = accumulatedContent.length - previousLength;

                                            logger.debug('[AIBrainService] Processed content chunk', {
                                                chunkCount,
                                                accumulatedLength: accumulatedContent.length,
                                                newChunkLength,
                                                isNewChunk: newChunkLength > 0,
                                                recoverableErrors,
                                                catastrophicErrors
                                            });

                                            if (options?.onChunk) {
                                                options.onChunk(content);
                                            }
                                        }

                                        // Handle metadata events
                                        if (parsed.type && parsed.type !== 'content' && parsed.type !== 'done') {
                                            localMetadataCount++;
                                            const metaType = parsed.type;
                                            const allowedStatusMessages = [
                                                'Loading context...',
                                                'Loading intelligence context...',
                                                'Intelligence context loaded',
                                                'Loading multimodal context...',
                                                'Multimodal context loaded',
                                                'Initializing...'
                                            ];
                                            const isStatusNoise =
                                                metaType === 'status' &&
                                                typeof parsed.message === 'string' &&
                                                parsed.message.length > 0 &&
                                                !allowedStatusMessages.includes(parsed.message);
                                            const isMetaNoise =
                                                metaType === 'meta' &&
                                                !parsed.reasoning &&
                                                !parsed.toolCall &&
                                                !parsed.toolResult;
                                            if (isStatusNoise || isMetaNoise) {
                                                continue;
                                            }

                                            logger.debug('[AIBrainService] Processed metadata event', {
                                                metadataCount,
                                                metaType,
                                                hasToolCall: !!parsed.toolCall,
                                                hasReasoning: !!parsed.reasoning
                                            });

                                            if (options?.onMetadata) {
                                                options.onMetadata({ ...parsed, type: typeof parsed.type === 'string' ? parsed.type : String(parsed.type ?? '') });
                                            }

                                            // Accumulate metadata for final result
                                            if (!finalResult) {
                                                finalResult = {
                                                    success: true,
                                                    output: accumulatedContent,
                                                    agent: 'Unknown',
                                                    model: '',
                                                    metadata: {}
                                                };
                                            }

                                            // Merge metadata events
                                            if (parsed.toolCall) {
                                                if (!finalResult.metadata) finalResult.metadata = {};
                                                if (!Array.isArray(finalResult.metadata.tools)) {
                                                    finalResult.metadata.tools = [];
                                                }
                                                (finalResult.metadata.tools as unknown[]).push(parsed.toolCall);
                                            }
                                            if (parsed.toolResult) {
                                                if (!finalResult.metadata) finalResult.metadata = {};
                                                if (!Array.isArray(finalResult.metadata.tools)) {
                                                    finalResult.metadata.tools = [];
                                                }
                                                (finalResult.metadata.tools as unknown[]).push(parsed.toolResult);
                                            }
                                            if (parsed.reasoning) {
                                                if (!finalResult.metadata) finalResult.metadata = {};
                                                finalResult.metadata.reasoning = parsed.reasoning;
                                            }
                                        }

                                        // Handle stream completion
                                        if (parsed.type === 'done') {
                                            acceptEmbeddedSessionProjection(
                                                parsed,
                                                'AIBrainService.chatStream:done'
                                            );
                                            logger.debug('[AIBrainService] Stream complete', {
                                                totalChunks: localChunkCount,
                                                totalMetadata: localMetadataCount,
                                                finalLength: accumulatedContent.length,
                                                agent: parsed.agent,
                                                recoverableErrors: localRecoverableErrors,
                                                catastrophicErrors: localCatastrophicErrors
                                            });

                                            // Update metrics for successful completion
                                            this.streamingMetrics.successfulStreams++;
                                            this.streamingMetrics.recoverableErrors += localRecoverableErrors;
                                            this.streamingMetrics.averageChunkCount =
                                                (this.streamingMetrics.averageChunkCount * (this.streamingMetrics.successfulStreams - 1) + localChunkCount) / this.streamingMetrics.successfulStreams;
                                            this.streamingMetrics.averageMetadataCount =
                                                (this.streamingMetrics.averageMetadataCount * (this.streamingMetrics.successfulStreams - 1) + localMetadataCount) / this.streamingMetrics.successfulStreams;
                                            this.streamingMetrics.lastStreamDuration = Date.now() - streamStartTime;
                                            this.streamingMetrics.lastStreamSuccess = true;

                                            const finalOutput =
                                                parsed && typeof parsed === 'object' && 'output' in parsed && typeof (parsed as Record<string, unknown>).output === 'string' && (parsed as Record<string, unknown>).output?.toString().trim()
                                                    ? String((parsed as Record<string, unknown>).output)
                                                    : accumulatedContent;

                                            // Do NOT call options?.onChunk?.(finalOutput) here — it creates a ghost
                                            // streaming item in App that never finalizes (Bug 1). Final text is
                                            // delivered via return value and the last content chunk.

                                            finalResult = {
                                                success: true,
                                                output: finalOutput,
                                                agent: typeof parsed.agent === 'string' ? parsed.agent : 'Unknown',
                                                model: typeof parsed.model === 'string' ? parsed.model : '',
                                                metadata: {
                                                    ...(finalResult?.metadata || {}),
                                                    ...(parsed.metadata || {}),
                                                    groundingMetadata: (parsed as { metadata?: { groundingMetadata?: unknown } }).metadata?.groundingMetadata
                                                }
                                            };

                                            // Emit groundingMetadata so client can show "Sources used" (Bug 2)
                                            const doneGrounding = parsed && typeof parsed === 'object' && 'metadata' in parsed
                                                ? (parsed as Record<string, unknown>).metadata as Record<string, unknown> | undefined
                                                : undefined;
                                            const groundingMetadata = doneGrounding?.groundingMetadata;
                                            if (groundingMetadata && options?.onMetadata) {
                                                options.onMetadata({ type: 'groundingMetadata', groundingMetadata });
                                            }
                                        }
                                    }
                                }
                            }
                        }
                    }
                } catch (streamError) {
                    localRecoverableErrors++;
                    logger.warn('[AIBrainService] Stream processing error, attempting graceful degradation', {
                        error: streamError instanceof Error ? streamError.message : String(streamError),
                        recoverableErrors: localRecoverableErrors,
                        catastrophicErrors: localCatastrophicErrors,
                        accumulatedContentLength: accumulatedContent.length,
                        hasBufferedContent: sseParser.hasBufferedContent()
                    });

                    // Update partial success metrics
                    this.streamingMetrics.partialStreams++;
                    this.streamingMetrics.recoverableErrors += localRecoverableErrors;
                    this.streamingMetrics.lastStreamDuration = Date.now() - streamStartTime;
                    this.streamingMetrics.lastStreamSuccess = false;

                    // If we have accumulated content, return it instead of failing completely
                    if (fullAccumulatedText.length > 0 || finalResult) {
                        logger.debug('[AIBrainService] Graceful degradation: returning fullAccumulatedText');
                        return finalResult || {
                            success: true,
                            output: fullAccumulatedText,
                            agent: 'Unknown',
                            model: '',
                            metadata: {
                                streamingInterrupted: true,
                                recoverableErrors: localRecoverableErrors,
                                catastrophicErrors: localCatastrophicErrors
                            }
                        };
                    }

                    // Update failed metrics if no content to return
                    this.streamingMetrics.failedStreams++;
                    // Only throw if we have nothing to return
                    throw streamError;
                }
            }

            // Return final result or accumulated content
            if (finalResult) {
                return finalResult;
            }

            // Update metrics for stream that ended without 'done' event
            this.streamingMetrics.partialStreams++;
            this.streamingMetrics.recoverableErrors += localRecoverableErrors;
            this.streamingMetrics.lastStreamDuration = Date.now() - streamStartTime;
            this.streamingMetrics.lastStreamSuccess = true;

            return {
                success: true,
                output: accumulatedContent,
                agent: 'Unknown',
                model: '',
                metadata: {
                    streamingEndedWithoutDone: true,
                    recoverableErrors: localRecoverableErrors,
                    catastrophicErrors: localCatastrophicErrors
                }
            };
        } catch (error: any) {
            // Update metrics for failed streams
            this.streamingMetrics.failedStreams++;
            this.streamingMetrics.lastStreamDuration = Date.now() - streamStartTime;
            this.streamingMetrics.lastStreamSuccess = false;

            // Detect connection refused / network errors
            const errorMsg = error?.message || '';
            const errorName = typeof error?.name === 'string' ? error.name : '';
            const isAbortError =
                errorName === 'AbortError' ||
                /abort|aborted|signal is aborted/i.test(errorMsg);

            if (isAbortError) {
                logger.warn('[AIBrainService] chatStream aborted', {
                    reason: errorMsg || errorName || 'AbortError',
                });
                return {
                    success: false,
                    error: 'Request cancelled'
                };
            }

            const isConnectionRefused =
                errorMsg.includes('ECONNREFUSED') ||
                errorMsg.includes('Failed to fetch') ||
                errorMsg.includes('NetworkError') ||
                error?.code === 'ECONNREFUSED';

            if (isConnectionRefused) {
                const isDev = typeof window !== 'undefined' &&
                    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1');

                const helpMessage = isDev
                    ? 'API server not running. Start it with: pnpm dev:api:3002'
                    : 'Unable to connect to API server. Please try again later.';

                logger.warn('[AIBrainService] Connection error in chatStream', { isDev, originalError: errorMsg });
                return {
                    success: false,
                    error: helpMessage
                };
            }

            logger.error('[AIBrainService] Error in chatStream', error instanceof Error ? error : undefined);
            return {
                success: false,
                error: error.message || 'Unknown error occurred'
            };
        }
    }

    /**
     * Convert transcript history to messages format expected by the API
     */
    static transcriptToMessages(transcript: TranscriptItem[]): Array<{ role: string; content: string; attachments?: Array<{ mimeType: string; data: string }> }> {
        return transcript
            .filter(item => (item.text?.trim() || item.attachment) && item.isFinal !== false)
            .map(item => {
                const msg: { role: string; content: string; attachments?: Array<{ mimeType: string; data: string }> } = {
                    // Preserve 'model' role as expected by downstream tests/orchestrator
                    role: item.role === 'user' ? 'user' : 'model',
                    content: item.text || '', // Ensure content is never undefined
                };

                if (item.attachment?.data && item.attachment?.mimeType) {
                    msg.attachments = [{
                        mimeType: item.attachment.mimeType,
                        data: item.attachment.data
                    }];
                }

                return msg;
            });
    }
}
